"use client";

import { useEffect, useState } from "react";

import { useRouter } from "next/navigation";

import { Area, Need } from "@prisma/client";
import {
  Calendar,
  ExternalLink,
  Heart,
  Mail,
  MapPin,
  Package,
  Phone,
  Truck,
  User,
  Users,
} from "lucide-react";
import { Control, UseFormSetValue, UseFormWatch } from "react-hook-form";

import { PageFormTemplate } from "../components/PageFormTemplate";

import {
  FormCheckbox,
  FormInput,
  FormRadioGroup,
  FormSelect,
} from "@/components/forms/components/FormInputs";
import { ServiceAreaSelector } from "@/components/forms/components/ServiceAreaSelector";
import { HelpfulLinkPreview } from "@/components/needs/HelpfulLinkPreview";
import { claimNeedAction } from "@/lib/actions/claims";
import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import type { ActionResponse } from "@/lib/utils/errors";
import { ClaimFormData, createClaimSchema } from "@/lib/validations/claims";

/**
 * ClaimForm component for users to claim a need
 * Features enhanced accessibility including:
 * - Proper form validation with descriptive error messages
 * - ARIA live regions for dynamic content updates
 * - Proper labeling of form controls
 * - Status announcements for screen readers
 * - Keyboard navigation support
 * - Partial claiming for multi-item needs
 */

type NeedWithExtras = Need & {
  tags?: { id: string; name: string }[];
  helpfulLinks?: string[];
  dropoffLocation?: string | null;
  shippingAddress?: string | null;
  area?: { name: string; state?: string | null } | null;
  dueDate?: Date | null;
  impactDescription?: string | null;
};

interface ClaimFormProps {
  need: NeedWithExtras;
  claimNeed: typeof claimNeedAction;
  checkAvailability?: (
    needId: string,
  ) => Promise<{ isClaimable: boolean; remainingQuantity: number }>;
  callbackUrl?: string;
  areas?: Area[];
  organizationSlug: string;
  remainingQuantity?: number;
  checkSubscription?: (
    orgId: string,
    email: string,
  ) => Promise<{
    exists: boolean;
    isSubscribed: boolean;
    volunteer?: {
      email: string;
      firstName: string;
      lastName: string;
      phone?: string;
    };
  }>;
}

function getNeedTypeLabel(needType: string): string {
  if (needType === "FINANCIAL_ASSISTANCE") return "Amount";
  if (needType === "PEOPLE_NEEDED") return "People";

  return "Items";
}

function getNeedQuantityDisplay(
  needType: string,
  targetQuantity: number,
  remainingQuantity?: number,
): string {
  if (needType === "FINANCIAL_ASSISTANCE") {
    return remainingQuantity !== undefined
      ? `$${(remainingQuantity / 100).toFixed(2)} remaining`
      : `$${(targetQuantity / 100).toFixed(2)} needed`;
  }

  return remainingQuantity !== undefined
    ? `${remainingQuantity} remaining`
    : `${targetQuantity} needed`;
}

function isNonEmptyString(val?: string | null): boolean {
  return Boolean(val) && val!.trim().length > 0;
}

function DropoffCard({ location }: { location: string }) {
  return (
    <div className="p-4 sm:p-5 bg-default-50 rounded-lg border border-default-200">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="p-2 bg-white rounded-lg border border-default-200 flex-shrink-0">
          <MapPin aria-hidden="true" className="w-5 h-5 text-default-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm sm:text-base font-semibold text-default-900 mb-1.5">
            Drop-off Location
          </div>
          <div className="text-sm sm:text-base text-default-600 leading-relaxed break-words">
            {location}
          </div>
        </div>
      </div>
    </div>
  );
}

function ShippingCard({ address }: { address: string }) {
  return (
    <div className="p-4 sm:p-5 bg-default-50 rounded-lg border border-default-200">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="p-2 bg-white rounded-lg border border-default-200 flex-shrink-0">
          <Truck aria-hidden="true" className="w-5 h-5 text-default-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm sm:text-base font-semibold text-default-900 mb-1.5">
            Shipping Address
          </div>
          <div className="text-sm sm:text-base text-default-600 whitespace-pre-line leading-relaxed break-words">
            {address}
          </div>
        </div>
      </div>
    </div>
  );
}

function NeedDeliveryInstructions({
  dropoffLocation,
  shippingAddress,
}: {
  dropoffLocation?: string | null;
  shippingAddress?: string | null;
}) {
  const hasDropoff = isNonEmptyString(dropoffLocation);
  const hasShipping = isNonEmptyString(shippingAddress);

  if (!hasDropoff && !hasShipping) return null;

  return (
    <div className="pt-4 border-t border-default-200">
      <h4 className="text-base sm:text-lg font-semibold mb-4 sm:mb-5">
        Delivery Instructions
      </h4>
      <div className="space-y-4 sm:space-y-5">
        {hasDropoff && <DropoffCard location={dropoffLocation!} />}
        {hasShipping && <ShippingCard address={shippingAddress!} />}
      </div>
    </div>
  );
}

function NeedHelpfulLinks({ helpfulLinks }: { helpfulLinks?: string[] }) {
  const validLinks =
    helpfulLinks?.filter((link) => link.trim().length > 0) ?? [];

  if (validLinks.length === 0) return null;

  return (
    <div className="pt-4 border-t border-default-200">
      <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center gap-2">
        <ExternalLink aria-hidden="true" className="w-5 h-5 flex-shrink-0" />
        Helpful Links
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {validLinks.map((link, index) => (
          <HelpfulLinkPreview key={index} url={link} />
        ))}
      </div>
    </div>
  );
}

/**
 * Component that displays the need being claimed
 */
function NeedDetails({
  need,
  remainingQuantity,
}: {
  need: NeedWithExtras;
  remainingQuantity?: number;
}) {
  return (
    <div
      className="mb-6 space-y-4 sm:space-y-6"
      data-testid="claim-need-details"
    >
      <div className="space-y-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold mb-3 text-default-900">
            {need.title}
          </h3>
          <p className="text-default-600 text-base sm:text-lg leading-relaxed">
            {need.description}
          </p>
        </div>

        {need.impactDescription && (
          <div className="p-4 sm:p-5 bg-primary-50 rounded-lg border-l-4 border-primary">
            <h4 className="text-base sm:text-lg font-semibold mb-2 flex items-center gap-2 text-primary-900">
              <Heart aria-hidden="true" className="w-5 h-5 flex-shrink-0" />
              Who This Helps
            </h4>
            <p className="text-sm sm:text-base text-primary-800 whitespace-pre-line leading-relaxed">
              {need.impactDescription}
            </p>
          </div>
        )}
      </div>

      <NeedQuickFacts need={need} remainingQuantity={remainingQuantity} />

      {need.tags && need.tags.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2 text-sm font-medium text-default-700">
            <Users aria-hidden="true" className="w-4 h-4" />
            Tags
          </div>
          <div className="flex flex-wrap gap-2">
            {need.tags.map((tag) => (
              <span
                key={tag.id}
                className="text-xs sm:text-sm bg-default-100 text-default-700 px-3 py-1.5 rounded-full font-medium"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <NeedHelpfulLinks helpfulLinks={need.helpfulLinks} />
      <NeedDeliveryInstructions
        dropoffLocation={need.dropoffLocation}
        shippingAddress={need.shippingAddress}
      />
    </div>
  );
}

function NeedQuickFacts({
  need,
  remainingQuantity,
}: {
  need: NeedWithExtras;
  remainingQuantity?: number;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {need.area && (
        <div className="p-3 sm:p-4 bg-default-50 rounded-lg border border-default-200">
          <div className="flex items-center gap-2 mb-1.5">
            <MapPin
              aria-hidden="true"
              className="w-4 h-4 text-default-400 flex-shrink-0"
            />
            <span className="text-xs sm:text-sm font-medium text-default-500 uppercase tracking-wide">
              Area
            </span>
          </div>
          <p className="text-sm sm:text-base font-semibold text-default-900">
            {need.area.name}
            {need.area.state && `, ${need.area.state}`}
          </p>
        </div>
      )}

      {need.dueDate && (
        <div className="p-3 sm:p-4 bg-default-50 rounded-lg border border-default-200">
          <div className="flex items-center gap-2 mb-1.5">
            <Calendar
              aria-hidden="true"
              className="w-4 h-4 text-default-400 flex-shrink-0"
            />
            <span className="text-xs sm:text-sm font-medium text-default-500 uppercase tracking-wide">
              Due Date
            </span>
          </div>
          <p className="text-sm sm:text-base font-semibold text-default-900">
            {new Date(need.dueDate).toLocaleDateString("en-US", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </p>
        </div>
      )}

      {need.targetQuantity && (
        <div className="p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-1.5">
            <Package
              aria-hidden="true"
              className="w-4 h-4 text-green-600 flex-shrink-0"
            />
            <span className="text-xs sm:text-sm font-medium text-green-700 uppercase tracking-wide">
              {getNeedTypeLabel(need.needType)}
            </span>
          </div>
          <p className="text-sm sm:text-base font-semibold text-green-900">
            {getNeedQuantityDisplay(
              need.needType,
              need.targetQuantity,
              remainingQuantity,
            )}
          </p>
        </div>
      )}

      <div className="p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-xs sm:text-sm font-medium text-blue-700 uppercase tracking-wide">
            Priority
          </span>
        </div>
        <p className="text-sm sm:text-base font-semibold text-blue-900 capitalize">
          {need.urgency}
        </p>
      </div>
    </div>
  );
}

const VOLUNTEER_INFO_KEY = "needbridge_volunteer_info";

function loadVolunteerInfo(): Partial<ClaimFormData> {
  if (typeof window === "undefined") return {};

  try {
    const saved = localStorage.getItem(VOLUNTEER_INFO_KEY);

    if (!saved) return {};

    const parsed = JSON.parse(saved) as Partial<ClaimFormData>;

    return {
      email: parsed.email,
      firstName: parsed.firstName,
      lastName: parsed.lastName,
      phone: parsed.phone,
    };
  } catch {
    return {};
  }
}

function persistVolunteerInfo(data: ClaimFormData): void {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(
      VOLUNTEER_INFO_KEY,
      JSON.stringify({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
      }),
    );
  } catch {
    // ignore storage errors
  }
}

function initialQuantityForType(
  showQuantityInput: boolean,
  needType: string,
  targetType: string,
): string | undefined {
  return showQuantityInput && needType === targetType ? "1" : undefined;
}

function buildClaimDefaultValues(
  needId: string,
  need: NeedWithExtras,
  showQuantityInput: boolean,
  savedInfo: Partial<ClaimFormData>,
) {
  return {
    amount: initialQuantityForType(
      showQuantityInput,
      need.needType,
      "FINANCIAL_ASSISTANCE",
    ),
    email: savedInfo.email,
    estimatedValue: "",
    firstName: savedInfo.firstName,
    lastName: savedInfo.lastName,
    needId,
    peopleCount: initialQuantityForType(
      showQuantityInput,
      need.needType,
      "PEOPLE_NEEDED",
    ),
    phone: savedInfo.phone,
    quantity: initialQuantityForType(
      showQuantityInput,
      need.needType,
      "PHYSICAL_ITEM",
    ),
    subscribe: false,
  };
}

function buildPhysicalItemDescription(
  targetQuantity: number | null,
  remainingQuantity?: number,
): string {
  if (remainingQuantity !== undefined) {
    return `This need has ${remainingQuantity} items remaining. You can claim some or all remaining items.`;
  }
  const qty = targetQuantity ?? 1;
  const plural = qty !== 1 ? "s" : "";

  return `This need requires ${qty} item${plural} total. You can claim some or all items.`;
}

function buildFinancialDescription(
  targetQuantity: number | null,
  remainingQuantity?: number,
): string {
  if (remainingQuantity !== undefined) {
    return `This need has $${(remainingQuantity / 100).toFixed(2)} remaining. You can contribute some or all of the remaining amount.`;
  }

  return `This need requires $${((targetQuantity ?? 0) / 100).toFixed(2)} total. You can contribute some or all of the amount.`;
}

function buildPeopleDescription(
  targetQuantity: number | null,
  remainingQuantity?: number,
): string {
  if (remainingQuantity !== undefined) {
    return `This need has ${remainingQuantity} people remaining. You can commit some or all remaining people.`;
  }

  return `This need requires ${targetQuantity} people total. You can commit some or all people.`;
}

function ClaimQuantityInputs({
  control,
  maxQuantity,
  need,
  remainingQuantity,
  showQuantityInput,
}: {
  control: Control<ClaimFormData>;
  maxQuantity: number;
  need: NeedWithExtras;
  remainingQuantity?: number;
  showQuantityInput: boolean;
}) {
  if (!showQuantityInput) return null;

  if (need.needType === "PHYSICAL_ITEM") {
    return (
      <FormInput
        control={control}
        data-testid="claim-quantity-input"
        description={buildPhysicalItemDescription(
          need.targetQuantity,
          remainingQuantity,
        )}
        label="How Many Items Can You Provide?"
        max={maxQuantity}
        min={1}
        name="quantity"
        placeholder="Enter quantity"
        startContent={<Package className="h-4 w-4 text-default-400" />}
        tabIndex={0}
        type="number"
      />
    );
  }

  if (need.needType === "FINANCIAL_ASSISTANCE") {
    return (
      <FormInput
        control={control}
        data-testid="claim-amount-input"
        description={buildFinancialDescription(
          need.targetQuantity,
          remainingQuantity,
        )}
        label="Amount You Can Contribute (in dollars)"
        max={maxQuantity / 100}
        min={1}
        name="amount"
        placeholder="Enter amount"
        startContent={<Package className="h-4 w-4 text-default-400" />}
        tabIndex={0}
        type="number"
      />
    );
  }

  if (need.needType === "PEOPLE_NEEDED") {
    return (
      <FormInput
        control={control}
        data-testid="claim-people-count-input"
        description={buildPeopleDescription(
          need.targetQuantity,
          remainingQuantity,
        )}
        label="How Many People Can You Provide?"
        max={maxQuantity}
        min={1}
        name="peopleCount"
        placeholder="Enter number of people"
        startContent={<Users className="h-4 w-4 text-default-400" />}
        tabIndex={0}
        type="number"
      />
    );
  }

  return null;
}

function SubscriptionSection({
  areas,
  control,
  isSubscribed,
  setValue,
  watch,
}: {
  areas: Area[];
  control: Control<ClaimFormData>;
  isSubscribed: boolean;
  setValue: UseFormSetValue<ClaimFormData>;
  watch: UseFormWatch<ClaimFormData>;
}) {
  if (isSubscribed) {
    return (
      <p className="text-xs text-default-500">
        This email is already subscribed to notifications.
      </p>
    );
  }

  const isSubscribing = watch("subscribe");

  return (
    <>
      <FormCheckbox
        control={control}
        data-testid="claim-subscribe-checkbox"
        description="Get notified about future needs (you can unsubscribe anytime)"
        label="Subscribe to future needs"
        name="subscribe"
      />
      {isSubscribing && (
        <SubscriptionDetails
          areas={areas}
          control={control}
          setValue={setValue}
          watch={watch}
        />
      )}
    </>
  );
}

function SubscriptionDetails({
  areas,
  control,
  setValue,
  watch,
}: {
  areas: Area[];
  control: Control<ClaimFormData>;
  setValue: UseFormSetValue<ClaimFormData>;
  watch: UseFormWatch<ClaimFormData>;
}) {
  const isRequired = watch("subscribe");
  const areaOptions = [
    ...areas.map((a) => ({ label: `${a.name}, ${a.state}`, value: a.id })),
    { label: "Other (please specify)", value: "other" },
  ];

  return (
    <div className="space-y-4 border-t border-default-200 pt-4">
      <div>
        <h4 className="text-sm font-medium">Email Notification Frequency</h4>
        <p className="text-xs text-default-500">
          How often would you like to receive notifications?
        </p>
      </div>
      <FormRadioGroup
        control={control}
        name="frequency"
        options={[
          {
            label: "Immediate (all needs, plus critical alerts)",
            value: "immediate",
          },
          { label: "Daily summary (plus critical alerts)", value: "daily" },
          { label: "Weekly digest (plus critical alerts)", value: "weekly" },
        ]}
      />

      <div>
        <h4 className="text-sm font-medium">Address Information</h4>
        <p className="text-xs text-default-500">
          Helps us match needs in your area. You can adjust this later.
        </p>
      </div>
      <FormInput
        control={control}
        label="Street Address"
        name="streetAddress"
        placeholder="123 Main St"
        required={isRequired}
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FormInput
          control={control}
          label="City"
          name="city"
          placeholder="City"
          required={isRequired}
        />
        <FormInput
          control={control}
          label="State"
          name="state"
          placeholder="CA"
          required={isRequired}
        />
        <FormInput
          control={control}
          label="ZIP Code"
          name="zipCode"
          placeholder="12345"
          required={isRequired}
        />
      </div>

      <div>
        <h4 className="text-sm font-medium">Area Information</h4>
        <p className="text-xs text-default-500">
          Tell us where you live and where you can help
        </p>
      </div>
      <FormSelect
        control={control}
        data-testid="residence-area-select"
        label="Where do you live?"
        name="residenceAreaId"
        options={areaOptions}
        placeholder="Select your area of residence"
        required={isRequired}
      />
      {watch("residenceAreaId") === "other" && (
        <div className="mt-3">
          <FormInput
            control={control}
            data-testid="custom-residence-area-input"
            label="Please specify your area of residence"
            name="customResidenceArea"
            placeholder="e.g., Springfield, IL"
            required={isRequired}
          />
        </div>
      )}
      <ServiceAreaSelector
        areas={areas}
        data-testid="service-areas-selector"
        selectedAreaIds={watch("serviceAreas") ?? []}
        onSelectionChange={(ids) =>
          setValue("serviceAreas", ids, { shouldValidate: true })
        }
      />
    </div>
  );
}

function resolveClaimQuantity(
  remainingQuantity?: number,
  targetQuantity?: number | null,
): number {
  if (remainingQuantity !== undefined) return remainingQuantity;
  if (targetQuantity != null) return targetQuantity;

  return 1;
}

function buildClaimSchema(showQuantityInput: boolean, maxQuantity: number) {
  if (showQuantityInput && typeof maxQuantity === "number") {
    return createClaimSchema(maxQuantity);
  }

  return createClaimSchema(undefined);
}

function buildClaimPath(
  organizationSlug: string,
  needId: string,
  token?: string,
): string {
  if (token) return `/${organizationSlug}/needs/${needId}/claim/${token}`;

  return `/${organizationSlug}/needs/${needId}/claimed`;
}

function parseNumberOrNull(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
}

function getRequestedClaimQuantity(
  needType: string,
  data: ClaimFormData,
  fallbackTargetQuantity?: number | null,
): number {
  const fallbackQuantity = fallbackTargetQuantity ?? 1;
  const amount = parseNumberOrNull(data.amount) ?? 0;
  const peopleCount = parseNumberOrNull(data.peopleCount) ?? fallbackQuantity;
  const quantity = parseNumberOrNull(data.quantity) ?? fallbackQuantity;
  const requestedByType: Record<string, number> = {
    FINANCIAL_ASSISTANCE: Math.round(amount * 100),
    PEOPLE_NEEDED: Math.round(peopleCount),
  };

  return requestedByType[needType] ?? Math.round(quantity);
}

function buildExceededMessage(needType: string, remaining: number): string {
  if (needType === "FINANCIAL_ASSISTANCE") {
    return `Cannot contribute more than available. Only $${(remaining / 100).toFixed(2)} remains.`;
  }
  if (needType === "PEOPLE_NEEDED") {
    return `Cannot claim more people than available. Only ${remaining} people remain.`;
  }

  return `Cannot claim more items than available. Only ${remaining} items remain.`;
}

function buildAvailabilityChangedMessage(
  needType: string,
  remaining: number,
): string {
  if (needType === "FINANCIAL_ASSISTANCE") {
    return `Availability changed while you were filling out the form. Latest remaining amount: $${(remaining / 100).toFixed(2)}.`;
  }
  if (needType === "PEOPLE_NEEDED") {
    return `Availability changed while you were filling out the form. Latest remaining count: ${remaining} people.`;
  }

  return `Availability changed while you were filling out the form. Latest remaining count: ${remaining} items.`;
}

function isNeedUnavailable(latest: {
  isClaimable: boolean;
  remainingQuantity: number;
}): boolean {
  return !latest.isClaimable || latest.remainingQuantity <= 0;
}

function getAvailabilityMessage(
  remainingQuantity: number | undefined,
  latestRemainingQuantity: number,
  needType: string,
): string | null {
  if (typeof remainingQuantity !== "number") return null;
  if (latestRemainingQuantity === remainingQuantity) return null;

  return buildAvailabilityChangedMessage(needType, latestRemainingQuantity);
}

async function validateAvailabilityBeforeSubmit({
  checkAvailability,
  data,
  need,
  remainingQuantity,
}: {
  checkAvailability?: (
    needId: string,
  ) => Promise<{ isClaimable: boolean; remainingQuantity: number }>;
  data: ClaimFormData;
  need: NeedWithExtras;
  remainingQuantity?: number;
}): Promise<{ availabilityMessage: string | null; error: string | null }> {
  if (!checkAvailability) return { availabilityMessage: null, error: null };

  const latest = await checkAvailability(need.id);

  if (isNeedUnavailable(latest)) {
    return {
      availabilityMessage: null,
      error: "This need is no longer available for claiming",
    };
  }

  const requestedQuantity = getRequestedClaimQuantity(
    need.needType,
    data,
    need.targetQuantity,
  );
  const availabilityMessage = getAvailabilityMessage(
    remainingQuantity,
    latest.remainingQuantity,
    need.needType,
  );

  if (requestedQuantity > latest.remainingQuantity) {
    return {
      availabilityMessage,
      error: buildExceededMessage(need.needType, latest.remainingQuantity),
    };
  }

  return { availabilityMessage, error: null };
}

/**
 * Claim form component
 */
export function ClaimForm({
  areas = [],
  checkAvailability,
  checkSubscription,
  claimNeed,
  need,
  organizationSlug,
  remainingQuantity,
}: ClaimFormProps) {
  const router = useRouter();
  const needId = need.id;
  const organizationId = need.organizationId;

  const hasItemCount = need.targetQuantity != null;
  const maxQuantity = resolveClaimQuantity(
    remainingQuantity,
    need.targetQuantity,
  );
  const showQuantityInput = hasItemCount;
  const formSchema = buildClaimSchema(showQuantityInput, maxQuantity);

  const [isAlreadySubscribed, setIsAlreadySubscribed] =
    useState<boolean>(false);
  const [volunteerInfo, setVolunteerInfo] = useState<{
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
  } | null>(null);
  const [availabilityMessage, setAvailabilityMessage] = useState<string | null>(
    null,
  );

  const savedInfo = volunteerInfo ?? loadVolunteerInfo();

  const handleFormSubmit = async (data: ClaimFormData) => {
    if (!needId) {
      return { error: "Missing need ID", success: false };
    }

    const availabilityValidation = await validateAvailabilityBeforeSubmit({
      checkAvailability,
      data,
      need,
      remainingQuantity,
    });

    setAvailabilityMessage(availabilityValidation.availabilityMessage);
    if (availabilityValidation.error) {
      return { error: availabilityValidation.error, success: false as const };
    }

    persistVolunteerInfo(data);

    return await claimNeed(needId, data);
  };

  const handleError = (error: Error) => {
    console.error("Claim form error:", error);

    void EventPublisher.publishError(EventKeys.VOLUNTEER_ERROR, error, {
      needId,
      needTitle: need.title,
      organizationId: need.organizationId,
    });
  };

  const handleSuccess = (result: ActionResponse<{ claimToken?: string }>) => {
    const token = result.data?.claimToken;

    console.log("RESULT", result, result.data);
    const path = buildClaimPath(organizationSlug, needId, token);

    router.push(path);
  };

  return (
    <PageFormTemplate
      data-testid="claim-form"
      defaultValues={buildClaimDefaultValues(
        needId,
        need,
        showQuantityInput,
        savedInfo,
      )}
      description="Complete the form below to help with this need"
      eventKeys={{
        attempt: EventKeys.VOLUNTEER_NEED_CLAIM_ATTEMPT,
        failure: EventKeys.VOLUNTEER_NEED_CLAIM_FAILURE,
        success: EventKeys.VOLUNTEER_NEED_CLAIM_SUCCESS,
      }}
      formSchema={formSchema}
      icon="Heart"
      submitLabel="Claim this Need"
      title="I Can Help"
      onError={handleError}
      onSubmit={handleFormSubmit}
      onSuccess={handleSuccess}
    >
      {({
        control,
        setValue,
        watch,
      }: {
        control: Control<ClaimFormData>;
        setValue: UseFormSetValue<ClaimFormData>;
        watch: UseFormWatch<ClaimFormData>;
      }) => (
        <ClaimFormFields
          areas={areas}
          availabilityMessage={availabilityMessage}
          checkSubscription={checkSubscription}
          control={control}
          isAlreadySubscribed={isAlreadySubscribed}
          maxQuantity={maxQuantity}
          need={need}
          organizationId={organizationId}
          remainingQuantity={remainingQuantity}
          setIsAlreadySubscribed={setIsAlreadySubscribed}
          setValue={setValue}
          setVolunteerInfo={setVolunteerInfo}
          showQuantityInput={showQuantityInput}
          watch={watch}
        />
      )}
    </PageFormTemplate>
  );
}

interface SubscriptionCallbacks {
  setIsAlreadySubscribed: (val: boolean) => void;
  setVolunteerInfo: (
    info: {
      email: string;
      firstName: string;
      lastName: string;
      phone?: string;
    } | null,
  ) => void;
  setValue: UseFormSetValue<ClaimFormData>;
}

function applyVolunteerInfo(
  volunteer: {
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
  },
  callbacks: SubscriptionCallbacks,
): void {
  callbacks.setVolunteerInfo(volunteer);
  callbacks.setValue("firstName", volunteer.firstName, {
    shouldValidate: false,
  });
  callbacks.setValue("lastName", volunteer.lastName, { shouldValidate: false });
  if (volunteer.phone) {
    callbacks.setValue("phone", volunteer.phone, { shouldValidate: false });
  }
}

async function checkEmailSubscription(
  email: string,
  checkSubscription: ClaimFormProps["checkSubscription"],
  organizationId: string,
  callbacks: SubscriptionCallbacks,
): Promise<void> {
  const trimmed = email.trim().toLowerCase();

  if (!trimmed.includes("@")) {
    callbacks.setIsAlreadySubscribed(false);
    callbacks.setVolunteerInfo(null);

    return;
  }

  if (!checkSubscription) return;

  try {
    const data = await checkSubscription(organizationId, trimmed);

    callbacks.setIsAlreadySubscribed(Boolean(data.isSubscribed));
    if (data.isSubscribed) {
      callbacks.setValue("subscribe", false, { shouldValidate: true });
    }

    if (data.volunteer) {
      applyVolunteerInfo(data.volunteer, callbacks);
    } else {
      callbacks.setVolunteerInfo(null);
    }
  } catch {
    // ignore network errors
  }
}

function ClaimFormFields({
  areas,
  availabilityMessage,
  checkSubscription,
  control,
  isAlreadySubscribed,
  maxQuantity,
  need,
  organizationId,
  remainingQuantity,
  setIsAlreadySubscribed,
  setValue,
  setVolunteerInfo,
  showQuantityInput,
  watch,
}: {
  availabilityMessage?: string | null;
  areas: Area[];
  checkSubscription?: ClaimFormProps["checkSubscription"];
  control: Control<ClaimFormData>;
  isAlreadySubscribed: boolean;
  maxQuantity: number;
  need: NeedWithExtras;
  organizationId: string;
  remainingQuantity?: number;
  setIsAlreadySubscribed: (val: boolean) => void;
  setVolunteerInfo: (
    info: {
      email: string;
      firstName: string;
      lastName: string;
      phone?: string;
    } | null,
  ) => void;
  setValue: UseFormSetValue<ClaimFormData>;
  showQuantityInput: boolean;
  watch: UseFormWatch<ClaimFormData>;
}) {
  const email = watch("email") ?? "";

  useEffect(() => {
    const controller = new AbortController();
    const id = setTimeout(() => {
      void checkEmailSubscription(email, checkSubscription, organizationId, {
        setIsAlreadySubscribed,
        setValue,
        setVolunteerInfo,
      });
    }, 350);

    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [
    checkSubscription,
    email,
    organizationId,
    setIsAlreadySubscribed,
    setVolunteerInfo,
    setValue,
  ]);

  return (
    <>
      <NeedDetails need={need} remainingQuantity={remainingQuantity} />
      <p aria-hidden className="sr-only" data-testid="claim-debug">
        targetQuantity:{String(need.targetQuantity ?? "undefined")} | needType:
        {String(need.needType)} | max:
        {String(maxQuantity)} | showQty:{String(showQuantityInput)} | remaining:
        {String(remainingQuantity ?? "undefined")}
      </p>

      <div className="space-y-4">
        {availabilityMessage && (
          <div
            className="rounded-md border border-warning-300 bg-warning-50 p-3 text-sm text-warning-900"
            role="status"
          >
            {availabilityMessage}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            autoFocus
            required
            control={control}
            data-testid="claim-first-name-input"
            label="First Name"
            name="firstName"
            placeholder="Enter your first name"
            startContent={<User className="h-4 w-4 text-default-400" />}
            tabIndex={0}
          />

          <FormInput
            required
            control={control}
            data-testid="claim-last-name-input"
            label="Last Name"
            name="lastName"
            placeholder="Enter your last name"
            startContent={<User className="h-4 w-4 text-default-400" />}
            tabIndex={0}
          />
        </div>

        <FormInput
          required
          control={control}
          data-testid="claim-email-input"
          label="Email Address"
          name="email"
          placeholder="Enter your email address"
          startContent={<Mail className="h-4 w-4 text-default-400" />}
          tabIndex={0}
          type="email"
        />

        <FormInput
          required
          control={control}
          data-testid="claim-phone-input"
          label="Phone Number"
          name="phone"
          placeholder="Enter your phone number"
          startContent={<Phone className="h-4 w-4 text-default-400" />}
          tabIndex={0}
          type="tel"
        />

        <ClaimQuantityInputs
          control={control}
          maxQuantity={maxQuantity}
          need={need}
          remainingQuantity={remainingQuantity}
          showQuantityInput={showQuantityInput}
        />

        {need.needType === "PHYSICAL_ITEM" && (
          <FormInput
            control={control}
            data-testid="claim-estimated-value-input"
            description="Only you can legally estimate the value of your in-kind donation. Your estimate will be used on your tax receipt when we mark this donation as received."
            label="Estimated value of your donation (optional)"
            name="estimatedValue"
            placeholder="0.00"
            step="0.01"
            type="number"
          />
        )}

        <SubscriptionSection
          areas={areas}
          control={control}
          isSubscribed={isAlreadySubscribed}
          setValue={setValue}
          watch={watch}
        />
      </div>
    </>
  );
}
